import React, { useState, useMemo } from "react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { Trip } from "@/types/trip";
import logger from "@/utils/logger";
import { ViatorItem } from "./viatorItem";
import { SkeletonLoaderItem } from "../experienceUtils";
import { ViatorProductInitial } from "@/types/experiences";
import useAuthStore from "@/stores/user/authStore";
import { useRateLimitStore } from "@/stores/rateLimitStore";

import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ViatorProductListResponse } from "~/types/viator";
import useExperiencesStore from "@/stores/tripBuilder/useExperiences";

interface ViatorActivityListProps {
  updateTripData: (tripData: Trip) => void;
  status: string;
  isViatorFetching: boolean;
  viatorData: ViatorProductListResponse;
  viatorError: any;
  setViatorProduct: (product: any) => void;
  handleOpenModal: (placeId: string) => void;
  handleLoadMore: (type: "viator" | "waytora") => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const ViatorActivityList: React.FC<ViatorActivityListProps> = ({
  updateTripData,
  status,
  isViatorFetching,
  viatorData,
  viatorError,
  setViatorProduct,
  handleOpenModal,
  handleLoadMore,
  currentPage,
  setCurrentPage,
}) => {
  // auth store
  const anonymousUser = useAuthStore((state) => state.anonymousUser);

  // rate limit store

  // trip store
  const setRateLimit = useRateLimitStore((state) => state.setRateLimit);
  const tripData = useTripStore((state) => state.tripData);
  const checkIsAdmin = useTripStore((state) => state.checkIsAdmin);
  const addViatorExperienceToItinerary = useTripStore(
    (state) => state.addViatorExperienceToItinerary
  );
  const selectedDay = useTripStore((state) => state.selectedDay);
  const selectedLocation = useTripStore((state) => state.selectedLocation);
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);
  const getItineraryItemLength = useTripStore(
    (state) => state.getItineraryItemLength
  );

  // experience store
  const addExperienceToDay = useExperiencesStore(
    (state) => state.addExperienceToDay
  );
  const setAddingExperience = useExperiencesStore(
    (state) => state.setAddingExperience
  );

  // local state
  const [fetchingProduct, setFetchingProduct] = useState<string | null>(null);

  const handleLike = async (product_code: string) => {
    logger.log("Handling like for:", product_code);
    if (!tripData) return;
    const newLikedState = !tripData.liked_experiences.includes(product_code);
    updateTripData({
      ...tripData,
      liked_experiences: newLikedState
        ? [...tripData.liked_experiences, product_code]
        : tripData.liked_experiences.filter((id) => id !== product_code),
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleAddViatorExperienceToItinerary = async (
    experience: ViatorProductInitial
  ) => {
    if (!selectedDay || !selectedLocation) return;

    // if anonymous user and day experience length >=1, set rate limit
    if (
      anonymousUser &&
      getItineraryItemLength(selectedDay, selectedLocation) >= 1
    ) {
      setRateLimit(
        "Create your account to add more experiences to your trip.",
        tripData?.name
      );
      return;
    }
    setFetchingProduct(experience.product_code);
    addExperienceToDay(experience);

    try {
      await addViatorExperienceToItinerary(
        experience,
        selectedDay,
        selectedLocation.id
      );
    } catch (error) {
      console.error(
        "Error calling addViatorExperienceToItinerary from list:",
        error
      );
    } finally {
      setFetchingProduct(null);
      setAddingExperience(null);
    }
  };

  const handleNextPage = () => {
    if (anonymousUser) {
      setRateLimit("Create your account to view more experiences!");
      return;
    }
    if (viatorData?.nextCursor !== null) {
      handleLoadMore("viator");
      setCurrentPage(currentPage + 1);
    }
  };
  return (
    <React.Fragment>
      {status === "pending" ? (
        Array.from({ length: 6 }, (_, i) => i).map((_, index) => (
          <SkeletonLoaderItem key={index} />
        ))
      ) : status === "error" ? (
        <p>Error: {viatorError?.message}</p>
      ) : (
        <>
          {viatorData?.data.map((product: any, i: number) => (
            <React.Fragment key={i}>
              <ViatorItem
                key={product.product_code}
                product={product}
                isLiked={
                  tripData?.liked_experiences?.includes(product.product_code) ??
                  false
                }
                setViatorProduct={setViatorProduct}
                fetchingProduct={fetchingProduct === product.product_code}
                setIsLiked={handleLike}
                handleOpenModal={handleOpenModal}
                addToItinerary={handleAddViatorExperienceToItinerary}
                isAdmin={isAdmin}
              />
            </React.Fragment>
          ))}
          {isViatorFetching &&
            Array.from({ length: 4 }, (_, i) => i).map((_, index) => (
              <SkeletonLoaderItem key={`next-page-${index}`} />
            ))}

          {viatorData?.data && viatorData.data.length > 0 && (
            <div className="flex justify-center items-center gap-4 py-4 mt-2 bg-white border-t w-full col-span-full">
              <Button
                variant="outline"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-neutral-500 font-light px-2">
                Page {currentPage + 1}
              </span>
              <Button
                variant="outline"
                onClick={handleNextPage}
                disabled={
                  !viatorData?.data ||
                  viatorData.data.length === 0 ||
                  !viatorData.data[viatorData.data.length - 1] ||
                  viatorData.nextCursor === null
                }
                className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default ViatorActivityList;
